import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Validators } from "@angular/forms";
import { FileUpload } from "primeng/fileupload";
import { SubSink } from "subsink";
import { BASE_API_URL } from "../../../../core/environment.tokens";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { FormService } from "../../../../dynamic-forms/form.service";
import { CheckboxGroup } from "../../../../dynamic-forms/inputs/checkbox-group/checkbox-group.model";
import { DynamicInput } from "../../../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../../../dynamic-forms/inputs/selectable-input.model";
import { Textbox } from "../../../../dynamic-forms/inputs/textbox/textbox.model";
import { GridCommentsComponent } from "../../../../shared/comments/grid-comments/grid-comments.component";
import { BulkAction } from "../../../../shared/grid/bulk-actions/bulk-action.model";
import { GridView } from "../../../../shared/grid/grid-menu/grid-views/grid-view.model";
import { GridColumnDefinition } from "../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../shared/grid/models/grid-configuration.model";
import { GridFilter } from "../../../../shared/grid/models/grid-filter.model";
import { GridRequest } from "../../../../shared/grid/models/grid-request.model";
import { EditPendService } from "../../../../shared/pend/edit-pend.service";
import { PendStatus } from "../../../../shared/pend/pend-status.enum";
import { ArrayHelper } from "../../../../utilities/contracts/array-helper";
import { ActionButton } from "../../../../zdevcontrols/action-button/action-button.model";
import { DevControllerService } from "../../../../zdevcontrols/dev-controller/dev-controller.service";
import { AccessInfoViewEditService } from "../../retrieval/access-info-view-edit/access-info-view-edit.service";
import { FormType } from "../form-type.enum";
import { GridFormsComponent } from "../grid-forms/grid-forms.component";

@Component({
  selector: "app-attestation-pursuit-grid",
  templateUrl: "./attestation-pursuit-grid.component.html",
  styleUrls: ["./attestation-pursuit-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttestationPursuitGridComponent implements OnInit, OnDestroy {
  @Input() approvalRequestType: string;
  @Input() additionalBulkActions: BulkAction[] = [];

  private sink = new SubSink();

  @ViewChild("fileUploader") fileUploader: FileUpload;
  @ViewChild("commentsColumn", { static: true }) commentsColumn: TemplateRef<GridCommentsComponent>;
  @ViewChild(GridFormsComponent) gridForms!: GridFormsComponent;


  url = `${this.baseApiUrl}approvalCenter/attestation/grid`;
  attestationPursuitGridConfiguration = new GridConfiguration();
  attestationPursuitGridRequest: GridRequest;
  attestationPursuitGridSelection: any;
  actions: BulkAction[];
  refreshGrid = new EventEmitter<boolean>(true);
  refreshViews = new EventEmitter<GridView>(true);
  formData: FormData = new FormData();
  selectedFileList: any[] = [];
  selectedChaseRequests: any;
  isReleaseConfirmationModalVisible = false;

  get statusInput(): CheckboxGroup {
    return this.getInput("ApprovalStatusCsv") as CheckboxGroup;
  }
  set statusInput(value: CheckboxGroup) {
    this.setInput("ApprovalStatusCsv", value);
  }

  get pursuitTypeInput(): CheckboxGroup {
    return this.getInput("PursuitTypeCsv") as CheckboxGroup;
  }
  set pursuitTypeInput(value: CheckboxGroup) {
    this.setInput("PursuitTypeCsv", value);
  }

  get chaseIdInput(): Textbox {
    return this.getInput("ChaseId") as Textbox;
  }

  get AIDInput(): Textbox {
    return this.getInput("AID") as Textbox;
  }

  get memberFirstNameInput(): Textbox {
    return this.getInput("MemberFirstName");
  }

  get memberLastNameInput(): Textbox {
    return this.getInput("MemberLastName");
  }

  constructor(
    @Inject(BASE_API_URL) private readonly baseApiUrl: string,
    private readonly devService: DevControllerService,
    private readonly formService: FormService,
    private readonly changeDetector: ChangeDetectorRef,
    private messagingService: MessagingService,
    private accessInfoEditService: AccessInfoViewEditService,
    private readonly editPendService: EditPendService
  ) { }

  ngOnInit() {
    this.devService.push([new ActionButton({ name: "Refresh Grid", action: () => this.refreshGrid.emit() })]);
    this.createGrid();
    this.loadGrid();
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  createGrid(): void {
    this.attestationPursuitGridConfiguration = new GridConfiguration({
      columns: this.getColumns(),
      selectionMode: "multiple",
      pageSize: 10,
      pageSizeOptions: [10, 25, 50, 100],
    });
    this.actions = this.getActions(true);

  }

  private getColumns(): GridColumnDefinition[] {
    const defaultColumns = [
      new GridColumnDefinition({ field: "chaseId", header: "Original Chase ID", routeUrl: "/members/chase/:chaseId" }),
      new GridColumnDefinition({ field: "childChaseID", header: "ATT Chase ID", routeUrl: "/members/chase/:childChaseID" }),
      new GridColumnDefinition({ field: "projectName", header: "Project" }),
      new GridColumnDefinition({ field: "measureCode", header: "Measure" }),
      new GridColumnDefinition({ field: "memberFirstName", header: "First Name" }),
      new GridColumnDefinition({ field: "memberLastName", header: "LastName" }),
      new GridColumnDefinition({ field: "masterDocumentSourceID", header: "AID", routeUrl: "/retrieval/addressdetail/:masterDocumentSourceID" }),
      new GridColumnDefinition({ field: "childChaseCreatedDate", header: "Date Created" }),
      new GridColumnDefinition({ field: "pursuitApprovedBy", header: "Pursuit Approved by" }),
      new GridColumnDefinition({ field: "formNamesDisplay", header: "Forms", template: this.commentsColumn, showTitle: false, isSortableColumn: false }),
    ];

    return defaultColumns;
  }

  private getActions(showUploadForm?: boolean): BulkAction[] {
    const totalBulkActions = [
      ...this.additionalBulkActions,
      new BulkAction({
        name: "Upload Form",
        action: this.uploadAction,
        showBulkAction: showUploadForm,
      }),
      new BulkAction({
        name: "Release for retrieval",
        action: this.releaseForRetrievalConfirmation,
      }),
    ];

    const distinctBulkActions = totalBulkActions.filter((filter, i, filters) => {
      return filters.findIndex(a => a.name === filter.name) === i;
    });

    return distinctBulkActions;
  }

  private loadGrid(): void {
    this.attestationPursuitGridRequest = new GridRequest({
      url: this.url,
      filters: this.getFilters(),
    });
    this.getApprovalStatuses();
    this.getPursuitTypes();
    this.refreshGrid.emit();
  }

  private getFilters(): GridFilter[] {
    const memberValidator = [
      Validators.minLength(2),
      Validators.maxLength(100),
    ];
    const memberErrorMessage = {
      minlength: `Minimum 2 characters and Maximum 100 characters allowed`,
      maxlength: `Minimum 2 characters and Maximum 100 characters allowed`,
    };
    const totalFilters = [
      new GridFilter({
        input: new Textbox(),
        key: "ChaseId",
        name: "Chase ID",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "AID",
        name: "AID",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "MemberFirstName",
        name: "First Name",
        validators: memberValidator,
        errorMessages: memberErrorMessage,
      }),
      new GridFilter({
        input: new Textbox(),
        key: "MemberLastName",
        name: "Last Name",
        validators: memberValidator,
        errorMessages: memberErrorMessage,
      }),
      new GridFilter({
        input: new CheckboxGroup(),
        key: "ApprovalStatusCsv",
        name: "Status",
        show: false,
      }),
      new GridFilter({
        input: new CheckboxGroup(),
        key: "PursuitTypeCsv",
        name: "Pursuit Type",
        show: false,
      }),
    ];

    const distinctFilters = totalFilters.filter((filter, i, filters) => {
      return filters.findIndex(a => a.key === filter.key) === i;
    });

    return distinctFilters;
  }

  getInput<T extends DynamicInput>(key: string): T {
    if (this.attestationPursuitGridRequest == null) {
      return null;
    }

    return this.attestationPursuitGridRequest.getInput<T>(key);
  }

  setInput<T extends DynamicInput>(key: string, value: T): void {
    if (this.attestationPursuitGridRequest == null) {
      return null;
    }

    this.attestationPursuitGridRequest.setInput<T>(key, value);
  }

  trackByIndex(index, item): void {
    return index;
  }

  gridDataLoaded(data: any): void {
    this.changeDetector.markForCheck();
    this.attestationPursuitGridSelection = [];
  }

  uploadAction = (rowData: any): void => {
    this.selectedChaseRequests = ArrayHelper.isAvailable(rowData) ? rowData : [rowData];
    this.fileUploader.basicFileInput.nativeElement.click();
  }

  releaseForRetrievalConfirmation = (rowData: any): void => {
    this.selectedChaseRequests = ArrayHelper.isAvailable(rowData) ? rowData : [rowData];
    const isEveryRequestHasForms = this.selectedChaseRequests.every(chaseReq => chaseReq.forms.length > 0);
    if (!isEveryRequestHasForms) {
      this.isReleaseConfirmationModalVisible = true;
      return;
    }
    this.releaseForRetrievalAction();
  }

  releaseForRetrievalAction = (): void => {
    const editPendData = {
      chaseIds: this.selectedChaseRequests.map(chaseReq => chaseReq.childChaseID),
      chasePendIds: this.selectedChaseRequests.map(chaseReq => chaseReq.childChasePendID),
      owner: "C",
      notes: "Attestation released for retrieval",
      pendStatusId: PendStatus.Resolved,
    };
    this.editPendService.updatePend(editPendData).subscribe(
      () => {
        this.messagingService.showToast("Released for retrieval successfully.", SeverityType.SUCCESS);
        this.refreshGrid.emit();
      },
      () => {
        this.messagingService.showToast("Error while releasing for retrieval.", SeverityType.ERROR);
      }
    );
    this.selectedChaseRequests = null;
  }

  getApprovalStatuses(): void {
    const options = [
      new SelectableInput({ text: "Completed", value: "Completed" }),
      new SelectableInput({ text: "Auto-Completed", value: "Auto-Completed" }),
    ];
    this.statusInput = { ...this.statusInput, options, value: options } as any;
    this.formService.updateDom.next();
  }

  getPursuitTypes(): void {
    const options = [
      new SelectableInput({ text: "Attestation", value: "Attestation" }),
    ];
    this.pursuitTypeInput = { ...this.pursuitTypeInput, options, value: options } as any;
    this.formService.updateDom.next();
  }

  onFileSelect(event): void {
    for (const file of event.files) {
      const maxSize = 10485760; // 10 MB
      if (file.size > maxSize) {
        this.messagingService.showToast(`${file.name}: Maximum upload size is 10MB.`, SeverityType.ERROR);
      } else {
        this.selectedFileList.push(file);
        this.formData.append("Document", file);
      }
    }
  }

  uploadDocument(event, fileUpload): void {
    if (ArrayHelper.isAvailable(this.selectedFileList)) {

      this.accessInfoEditService.upload(this.formData, {
        chaseId: this.selectedChaseRequests[0].childChaseID,
        documentSourceId: this.selectedChaseRequests[0].masterDocumentSourceID,
        formType: FormType.Attestation,
      }).subscribe(
        data => {
          this.accessInfoEditService.refreshFunction();
          this.messagingService.showToast("Pursuit Attestation Form Uploaded successfully.", SeverityType.SUCCESS);
          this.formData = new FormData();
          this.selectedFileList = [];
          this.attestationPursuitGridSelection = [];
          this.changeDetector.markForCheck();
          if (!this.gridForms.isFormsModalVisible) {
            this.refreshGrid.emit();
          } else {
            this.gridForms.viewAllForms();
          }
          fileUpload.clear();
        },
        err => {
          this.messagingService.showToast("Error while uploading Form, please try again.", SeverityType.ERROR);
          this.formData = new FormData();
        }
      );
    } else {
      this.messagingService.showToast("Please select a required document.", SeverityType.ERROR);
    }
  }

  cancelConfirmation(event): void {
    this.attestationPursuitGridSelection = [];
    this.changeDetector.markForCheck();
  }

  onAttestationPursuitGridSelectionChange(event): void {
    this.actions = this.getActions(Boolean(event.length <= 1));
  }

  reloadGrid(): void {
    this.refreshGrid.emit();
  }

}
